import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase, supabaseChatGuest } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'admin';
  content: string;
}

// Ziyaretçi kimliği (tahmin edilemez, localStorage'da kalıcı) — konuşmayı eşlemek için.
const getVisitorId = (): string => {
  let v = localStorage.getItem('chat_visitor_id');
  if (!v) { v = (crypto as any).randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`; localStorage.setItem('chat_visitor_id', v); }
  return v;
};

// Yeni chat tabloları henüz üretilmiş Supabase tiplerinde yok → any ile erişim.
// NOT: db artık bileşen içinde seçilir — girişli kullanıcı ana istemci (auth.uid() RLS yolu),
// misafir supabaseChatGuest (x-visitor-id header'lı; RLS misafir konuşmasını bununla eşler).

const WHATSAPP_NUMBER = "905013614303";
const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;

const suggestedQuestions = [
  "Fiyatlar ve paketler neler?",
  "Nasıl video oluştururum?",
  "Hangi içerikleri üretebilirim?",
  "İlk video gerçekten ücretsiz mi?"
];

// Her oturumda farklı asistan ismi — tek kişi değil, bir ekip izlenimi.
const AGENT_NAMES = [
  "Merve", "Elif", "Zeynep", "Selin", "Buse", "Derya", "Ece", "Aslı",
  "Gizem", "Naz", "Ceren", "İrem", "Melis", "Sena", "Yağmur", "Beren",
];
const pickAgentName = () => AGENT_NAMES[Math.floor(Math.random() * AGENT_NAMES.length)];

// Keywords that trigger escalation
const escalationKeywords = [
  'yetkili', 'yönetici', 'müdür', 'insan', 'gerçek kişi',
  'canlı destek', 'temsilci', 'aktarır mısınız', 'aktarın',
  'bağlayın', 'bağlar mısınız', 'görüşmek istiyorum',
  'konuşmak istiyorum', 'birine bağla', 'birine aktarın'
];

const checkEscalation = (message: string): boolean => {
  const lower = message.toLowerCase();
  return escalationKeywords.some(k => lower.includes(k));
};

export const ChatWidget = () => {
  const { user } = useAuth();
  // Girişli → ana istemci (RLS auth.uid() yolu); misafir → x-visitor-id header'lı istemci
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = (user ? supabase : supabaseChatGuest) as any;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [escalationSent, setEscalationSent] = useState(false);
  const [showWhatsAppFallback, setShowWhatsAppFallback] = useState(false);
  // Kayıtlı konuşma yüklenirse DB'deki agent_name'e geçilir (başlık "Naz" derken
  // karşılama "ben Elif" demesin — isim tutarsızlığı bug'ı).
  const [agentName, setAgentName] = useState(pickAgentName);
  // Karşılama akışı: 0=bağlanıyor, 1=yazıyor (3 nokta), 2=mesaj hazır (canlı gelmiş gibi)
  const [greetState, setGreetState] = useState(0);
  // Dış karşılama baloncuğu — 2.5sn sonra çıkar, 10sn durur, kaybolur
  const [teaserVisible, setTeaserVisible] = useState(false);
  // Canlı sohbet (Faz 2): konuşma kaydı + admin devralma
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [takenOver, setTakenOver] = useState(false);
  const convIdRef = useRef<string | null>(null);
  const visitorIdRef = useRef<string>('');
  const adminNotifiedRef = useRef(false); // canlı sohbette ilk kullanıcı mesajında admin'e 1 kez bildirim

  // İlk yükleme: ziyaretçi kimliği + varsa önceki konuşmayı yükle.
  // Giriş yapmış kullanıcıda kayıtlı konuşma yoksa user_id ile en son konuşmayı BENİMSE
  // → admin'in kullanıcı panelinden başlattığı sohbet de kullanıcıya düşer.
  useEffect(() => {
    visitorIdRef.current = getVisitorId();
    if (convIdRef.current) return; // zaten yüklü
    (async () => {
      let saved = localStorage.getItem('chat_conversation_id');
      if (!saved && user?.id) {
        const { data: mine } = await db.from('chat_conversations')
          .select('id').eq('user_id', user.id).order('last_message_at', { ascending: false }).limit(1).maybeSingle();
        if ((mine as any)?.id) { saved = (mine as any).id; localStorage.setItem('chat_conversation_id', saved!); }
      }
      if (!saved) return;
      convIdRef.current = saved;
      setConversationId(saved);
      const { data } = await db.from('chat_messages')
        .select('id, role, content').eq('conversation_id', saved).order('created_at', { ascending: true });
      if (data && data.length) {
        setMessages(data.map((m: any) => ({ id: m.id, role: m.role, content: m.content })));
        setShowWelcome(false);
        setGreetState(2);
      }
      const { data: conv } = await db.from('chat_conversations')
        .select('taken_over, agent_name').eq('id', saved).maybeSingle();
      if ((conv as any)?.taken_over) setTakenOver(true);
      // Konuşma hangi isimle başladıysa o isimle devam et (rastgele yeni isim = tutarsızlık)
      if ((conv as any)?.agent_name) setAgentName((conv as any).agent_name);
    })();
  }, [user?.id]);

  // Realtime: admin yazınca anında düşsün + admin devralırsa AI sussun
  useEffect(() => {
    if (!conversationId) return;
    const ch = supabase
      .channel(`chat:${conversationId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `conversation_id=eq.${conversationId}` }, (payload: any) => {
        const m = payload.new;
        if (m.role === 'admin') {
          setShowWelcome(false);
          setGreetState(2);
          setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, { id: m.id, role: 'admin', content: m.content }]);
          setTakenOver(true);
          if (!isOpen) setTeaserVisible(true); // widget kapalıysa dikkat çek (admin yazdı)
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_conversations', filter: `id=eq.${conversationId}` }, (payload: any) => {
        if (payload.new?.taken_over) setTakenOver(true);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [conversationId]);

  // GÜVENLİK/KIRILMAZLIK: Misafir (anon) sohbet RLS'i artık visitor_id header'ına bağlı.
  // Anon realtime kanalı bu header'ı taşıyamadığı için admin→misafir anlık mesajlar
  // realtime yerine bu hafif polling ile gelir (yalnızca widget AÇIK ve anon iken).
  // Girişli kullanıcı ve admin için realtime aynen çalışır → onlarda polling yok.
  useEffect(() => {
    if (!isOpen || !conversationId || user) return; // yalnız anon misafir için fallback
    const poll = async () => {
      // Sadece admin mesajları — kendi user/assistant mesajlarını çoğaltma (realtime ile aynı davranış)
      const { data } = await db.from('chat_messages')
        .select('id, role, content').eq('conversation_id', conversationId)
        .eq('role', 'admin').order('created_at', { ascending: true });
      if (data && data.length) {
        setMessages(prev => {
          const have = new Set(prev.map(m => m.id));
          const add = (data as any[]).filter(m => !have.has(m.id))
            .map(m => ({ id: m.id, role: 'admin' as const, content: m.content }));
          if (!add.length) return prev;
          setShowWelcome(false); setGreetState(2);
          return [...prev, ...add];
        });
      }
      const { data: conv } = await db.from('chat_conversations')
        .select('taken_over').eq('id', conversationId).maybeSingle();
      if ((conv as any)?.taken_over) setTakenOver(true);
    };
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [isOpen, conversationId, user]);

  // Konuşma oluştur (ilk mesajda) + mesajı kaydet
  const ensureConversation = async (): Promise<string | null> => {
    if (convIdRef.current) return convIdRef.current;
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await db.from('chat_conversations')
      .insert({ visitor_id: visitorIdRef.current, user_id: user?.id ?? null, agent_name: agentName })
      .select('id').single();
    if (error || !data) return null;
    convIdRef.current = (data as any).id;
    localStorage.setItem('chat_conversation_id', (data as any).id);
    setConversationId((data as any).id);
    return (data as any).id;
  };

  const persistMessage = async (role: 'user' | 'assistant' | 'admin', content: string) => {
    try {
      const cid = await ensureConversation();
      if (!cid) return;
      await db.from('chat_messages').insert({ conversation_id: cid, role, content });
      await db.from('chat_conversations').update({
        last_message: content.slice(0, 140),
        last_message_at: new Date().toISOString(),
      }).eq('id', cid);
    } catch { /* sessiz */ }
  };

  // Sohbet ilk açıldığında canlı temsilci hissi: bağlanıyor → yazıyor → mesaj belirir
  useEffect(() => {
    if (!isOpen || messages.length > 0) return;
    setGreetState(0);
    const t1 = setTimeout(() => setGreetState(1), 1600);  // "yazıyor..." başlasın
    const t2 = setTimeout(() => {
      setGreetState(2);  // mesaj canlı gelmiş gibi belirsin
      // Konuşmayı OLUŞTUR + karşılamayı kaydet → admin ziyaretçiyi anında görsün (lead kaçmasın),
      // kullanıcı yazmadan da araya girebilsin. (Sadece önceki konuşma yoksa.)
      if (!convIdRef.current) {
        persistMessage('assistant', `Merhaba, ben ${agentName} 👋 UGC Video asistanınız. Size nasıl yardımcı olabilirim?`);
      }
    }, 3700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Dış karşılama baloncuğu: 2.5sn sonra göster, 10sn sonra gizle (sürekli durmasın)
  useEffect(() => {
    const t1 = setTimeout(() => setTeaserVisible(true), 2500);
    const t2 = setTimeout(() => setTeaserVisible(false), 12500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('chat_session_id')) {
      localStorage.setItem('chat_session_id', Date.now().toString());
    }
  }, []);

  const sendEscalationEmail = useCallback(async (customerMessage: string) => {
    if (escalationSent) return;
    try {
      const sessionId = localStorage.getItem('chat_session_id') || 'unknown';
      const { error } = await supabase.functions.invoke('chat-escalation', {
        body: { customerMessage, chatHistory: messages, sessionId }
      });
      if (!error) setEscalationSent(true);
    } catch (err) {
      console.error('Escalation error:', err);
    }
  }, [escalationSent, messages]);

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowWelcome(false);

    // Kullanıcı mesajını kaydet (konuşma oluşur + admin panelde görür)
    await persistMessage('user', content.trim());

    // İlk kullanıcı mesajında admin'e ANLIK bildirim (kelime şartı yok — hiçbir lead/destek kaçmasın)
    if (!adminNotifiedRef.current) {
      adminNotifiedRef.current = true;
      supabase.functions.invoke('send-push-notification', {
        body: { target: 'admins', type: 'live_chat', title: '💬 Canlı sohbette yeni mesaj', body: content.trim().slice(0, 90), url: '/admin/chat' },
      }).catch(() => {});
    }

    // Admin sohbeti devraldıysa AI cevap vermesin — canlı temsilci yanıtlar
    if (takenOver) {
      setIsLoading(false);
      return;
    }

    // Escalation check
    if (checkEscalation(content)) {
      await sendEscalationEmail(content);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Talebiniz yetkililerimize iletildi. En kısa sürede sizinle iletişime geçilecektir. Dilerseniz WhatsApp üzerinden de bize ulaşabilirsiniz. 📞'
      }]);
      setShowWhatsAppFallback(true);
      setIsLoading(false);
      return;
    }

    // Doğal his: önce mesajı "okur" (mavi tik), sonra "yazıyor…" — gerçek insan gibi bekler
    await new Promise((r) => setTimeout(r, 1500 + Math.random() * 1800));

    // Stream AI response
    const allMessages = [...messages, userMessage].map(m => ({ role: m.role, content: m.content }));
    let assistantContent = '';

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: allMessages, agentName }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error('AI yanıt veremedi');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantContent += delta;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantContent } : m);
                }
                return [...prev, { id: (Date.now() + 1).toString(), role: 'assistant', content: assistantContent }];
              });
              // İnsan gibi yazılsın — her parçada minik gecikme
              await new Promise((r) => setTimeout(r, 22));
            }
          } catch {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // If no content was generated
      if (!assistantContent.trim()) {
        throw new Error('Boş yanıt');
      }

      // AI cevabını kaydet
      await persistMessage('assistant', assistantContent);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Üzgünüm, şu an yanıt veremiyorum. Lütfen WhatsApp destek hattımızdan bize ulaşın. 👇'
      }]);
      setShowWhatsAppFallback(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Merhaba, UGC Video hakkında bilgi almak istiyorum.')}`;

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-20 right-5 md:bottom-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #E31B54 0%, #BE185D 50%, #9333EA 100%)',
              boxShadow: '0 8px 24px rgba(227, 27, 84, 0.4)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Dış karşılama baloncuğu — 10sn görünür, tıklayınca chat açılır */}
      <AnimatePresence>
        {!isOpen && teaserVisible && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            onClick={() => { setIsOpen(true); setTeaserVisible(false); }}
            className="fixed bottom-36 right-5 md:bottom-[5.5rem] z-50 max-w-[230px] cursor-pointer"
          >
            <div className="relative rounded-2xl rounded-br-md bg-background border border-border shadow-xl p-3 pr-7">
              <button onClick={(e) => { e.stopPropagation(); setTeaserVisible(false); }} className="absolute top-1.5 right-2 text-muted-foreground hover:text-foreground text-base leading-none">×</button>
              <p className="text-sm font-semibold">Merhaba 👋</p>
              <p className="mt-0.5 text-xs text-muted-foreground">Yardımcı olmamı ister misiniz? 💬</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-background rounded-2xl shadow-2xl border border-border overflow-hidden flex flex-col"
            style={{ height: '500px', maxHeight: 'calc(100vh - 2rem)' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-pink-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img src="/logo.png" alt="UGC Video" className="w-10 h-10 rounded-full object-cover bg-white p-0.5" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 ring-2 ring-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{agentName} · UGC Video</h3>
                  <p className="text-xs text-white/90 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    Çevrimiçi · genelde dakikalar içinde yanıtlar
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/20 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
              {showWelcome && messages.length === 0 && (
                <div className="space-y-4">
                  {/* 0: bağlanıyor — temsilciye bağlanma hissi */}
                  {greetState === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      Sizi bir müşteri temsilcimize bağlıyoruz, lütfen bekleyin…
                    </p>
                  )}
                  {/* 1: yazıyor (3 nokta) */}
                  {greetState === 1 && (
                    <div className="flex justify-start">
                      <div className="bg-background border border-border rounded-xl px-4 py-3 shadow-sm flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground mr-1">{agentName} yazıyor</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}
                  {/* 2: karşılama mesajı canlı gelmiş gibi belirir */}
                  {greetState >= 2 && (
                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <div className="bg-background rounded-xl p-4 shadow-sm border border-border">
                        <p className="text-sm text-foreground">
                          Merhaba, ben <span className="font-semibold text-primary">{agentName}</span> 👋 UGC Video asistanınız. Size nasıl yardımcı olabilirim? Fiyatlar, video oluşturma veya platform hakkında her şeyi sorabilirsiniz.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">Sık Sorulan Sorular:</p>
                        <div className="flex flex-wrap gap-2">
                          {suggestedQuestions.map((question, index) => (
                            <button
                              key={index}
                              onClick={() => sendMessage(question)}
                              className="text-xs bg-background border border-border rounded-full px-3 py-1.5 hover:bg-primary hover:text-white hover:border-primary transition-colors"
                            >
                              {question}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {messages.map((message, idx) => {
                const isUser = message.role === 'user';
                // Okundu (mavi tik): sonrasında bir yanıt geldiyse VEYA son mesajsa ve Elif "yazıyor"
                const read = isUser && (messages.slice(idx + 1).some((m) => m.role !== 'user') || (idx === messages.length - 1 && isLoading));
                return (
                <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                    isUser
                      ? 'bg-primary text-white'
                      : message.role === 'admin'
                        ? 'bg-emerald-50 border border-emerald-300 text-emerald-900 shadow-sm'
                        : 'bg-background border border-border text-foreground shadow-sm'
                  }`}>
                    {message.role === 'admin' && (
                      <div className="text-xs font-semibold text-emerald-600 mb-0.5 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {agentName} · Canlı Temsilci
                      </div>
                    )}
                    {message.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:m-0 [&>ul]:m-0 [&>ol]:m-0">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                    ) : message.content}
                    {isUser && (
                      <span className={`ml-2 inline-block align-bottom text-xs leading-none ${read ? 'text-sky-300' : 'text-white/60'}`}>✓✓</span>
                    )}
                  </div>
                </div>
                );
              })}

              {takenOver && (
                <div className="flex justify-center">
                  <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                    👤 {agentName} (canlı temsilci) sohbete katıldı
                  </div>
                </div>
              )}

              {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex justify-start">
                  <div className="bg-background border border-border rounded-xl px-4 py-3 shadow-sm flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground mr-1">{agentName} yazıyor</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* WhatsApp Fallback */}
            {showWhatsAppFallback && (
              <div className="px-3 pt-2 space-y-1">
                <p className="text-xs text-muted-foreground text-center">
                  İşin içinden çıkamıyorsanız WhatsApp hattımıza yazabilirsiniz 👇
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.90-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  +90 501 361 43 03 WhatsApp Destek
                </a>
              </div>
            )}

            {/* Always visible WhatsApp button */}
            {!showWhatsAppFallback && (
              <div className="px-3 pt-2">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.90-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp ile Yazın
                </a>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-3 border-t border-border bg-background">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Mesajınızı yazın..."
                  className="flex-1 bg-muted rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 border border-border"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="bg-primary text-white p-2.5 rounded-full hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
